import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef } from "@angular/core";
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NzTreeNodeOptions } from "ng-zorro-antd/tree";
import { Observable, takeUntil } from "rxjs";
import { IGoal } from "@gtmhub/goals/models";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { ISessionListItem, ISessionTreeNode } from "@webapp/sessions/models/session-tree.model";
import { Session } from "@webapp/sessions/models/sessions.model";
import { SessionSelectorFacade } from "@webapp/sessions/services/session-selector-facade.service";

const SESSION_SELECTOR_CONTROL_VALUE_ACCESSOR = { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SessionSelectorComponent), multi: true };

/**
 * Component for selecting sessions, currently used only in create okr form and implements logic for this use case only. Currently single select.
 * @example <gh-session-selector [goal]="goal" [parentId]="parentGoalOrMetric" [currentSession]="currentSession" [aria-labelledby]="'label'" [aria-required]="true" [aria-describedby]="'tooltip'" (select)="onSelect($event)"></gh-session-selector>
 * @param goal the goal that is under creation/edit
 * @param parentItem the metric or goal to which a child goal is being created
 * @param currentSession the session we are currently in or the host session of the goal
 * @param select emits the sessionId of the selected session to the parent component
 * @param ariaRequired announces if session field is required
 * @param ariaLabelledby announces the field label
 * @param ariaDescribedby announces the field description/tooltip
 * */

@Component({
  // There is an Angular.js component 'session-selector' and in session-selector.global.less we defined
  // global styles for this one, so we need to distinguish between the two components
  // eslint-disable-next-line webapp/no-component-selector-prefix
  selector: "gh-session-selector",
  templateUrl: "session-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService, SESSION_SELECTOR_CONTROL_VALUE_ACCESSOR],
})
export class SessionSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() public a11yLabelledby: string;
  @Input() public a11yRequired = false;
  @Input() public a11yDescribedby: string;
  @Input() public a11yDescription: string;
  @Input() public autofocus = false;
  @Input() public goal: Goal;
  @Input() public parentItem: Goal | Metric | IGoal;
  @Input() public currentSession: Session;
  @Input() public borderless: boolean;
  @Input() public uiId: string;
  @Input() public focusMe: boolean;
  @Input() public value: string;

  @Output() public readonly sessionSelect = new EventEmitter<string>();

  @HostBinding("class.borderless") public get borderlessClass(): boolean {
    return this.borderless;
  }

  public data: NzTreeNodeOptions[];
  public inputValue: string;
  public sessionForm: FormGroup;
  public isLoading$: Observable<boolean> = this.sessionSelectorFacade.isLoading$();
  public isTreeFlatten: boolean;
  private selectedIds: string[];
  private tree: ISessionTreeNode[] = [];
  private list: ISessionListItem[] = [];
  private notifyControlChange: (value: string) => void;

  constructor(private sessionSelectorFacade: SessionSelectorFacade, private formBuilder: FormBuilder, private destroyService$: DestroyService) {
    // form used in create okr form so allowing only single select for now
    this.sessionForm = this.formBuilder.group({
      id: this.formBuilder.control(""),
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      const value = this.value ?? "";
      this.sessionForm.controls.id.setValue(value, { emitEvent: false });
    }
    this.getData();
  }

  public ngOnInit(): void {
    this.sessionForm.controls.id.valueChanges.pipe(takeUntil(this.destroyService$)).subscribe((value) => {
      if (value !== this.value) {
        this.value = value;
        this.sessionSelect.emit(value);
        this.notifyControlChange?.(value);
      }
    });

    this.getData();
  }

  public getData(): void {
    this.sessionSelectorFacade
      .getSessionData$({
        goal: this.goal,
        parentItem: this.parentItem,
        currentSession: this.currentSession,
      })
      .pipe(takeUntil(this.destroyService$))
      .subscribe((results) => {
        this.tree = results.tree;
        this.list = results.list;
        this.data = this.tree;
        this.isTreeFlatten = false;
      });
  }

  public onOpenChange(): void {
    this.data = this.tree;
    this.isTreeFlatten = false;
  }

  public onInputValueChange(value: string): void {
    this.inputValue = value;
    this.setNodesStructure();
  }

  public searchFunction = (node: NzTreeNodeOptions): boolean => {
    return node.title.toLowerCase().includes(this.inputValue.toLowerCase());
  };

  public onSelectedValueChange(value: string[]): void {
    this.selectedIds = value;
    this.list.forEach((item) => {
      item.selected = this.selectedIds.includes(item.key);
    });

    this.tree.forEach((parentNode) => {
      this.setIsNodeSelected(parentNode);
    });
  }

  public writeValue(value: string): void {
    const normalizedValue = value ?? "";
    if (normalizedValue !== this.sessionForm.controls.id.value) {
      this.value = normalizedValue;
      this.sessionForm.controls.id.setValue(normalizedValue, { emitEvent: false });
    }
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.notifyControlChange = fn;
  }

  public registerOnTouched(): void {
    // required by the ControlValueAccessor interface
  }

  private setIsNodeSelected(parentNode: ISessionTreeNode): void {
    parentNode.selected = this.selectedIds.includes(parentNode.key);
    if (!parentNode.children.length) {
      return;
    }

    parentNode.children.forEach((childNode) => {
      this.setIsNodeSelected(childNode);
    });
  }

  private setNodesStructure(): void {
    if (this.inputValue.length) {
      this.data = this.list;
      this.isTreeFlatten = true;
    } else {
      this.data = this.tree;
      this.isTreeFlatten = false;
    }
  }
}
